import React, { useState, useEffect, useRef, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Avatar,
  Spin,
  Card,
  Tooltip,
  message as antMessage,
  App,
  Upload,
  Input,
  Popover,
} from "antd";
import {
  VideoCameraOutlined,
  AudioOutlined,
  AudioMutedOutlined,
  PhoneOutlined,
  UserOutlined,
  SendOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import agoraService from "../services/agoraService";
import { apiGetAppointmentDetails } from "../api/appointments";
import { apiJoinVideoCall } from "../api/videocall";
import { AppContext } from "../AppContext";
import socketService from "../services/socketService";
import * as messagesAPI from "../api/messages";

const VideoCall = () => {
  const { message } = App.useApp();
  const { appointmentId } = useParams();
  const navigate = useNavigate();

  const [isJoined, setIsJoined] = useState(false);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [callDuration, setCallDuration] = useState(0);
  const [appointment, setAppointment] = useState(null);
  const [localVideoTrack, setLocalVideoTrack] = useState(null); // Add this

  const localVideoRef = useRef(null);
  const remoteVideoRefs = useRef({});
  const callTimerRef = useRef(null);

  //messages
  const { state } = useContext(AppContext);
  const currentUser = state.currentUser;
  const currentUserId = currentUser?.id;

  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");
  const [sending, setSending] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const fetchAppointmentAndInitialize = async () => {
    try {
      const appointmentData = await apiGetAppointmentDetails(appointmentId);
      setAppointment(appointmentData);
      await initializeCall(appointmentData);
    } catch (error) {
      console.error("Error fetching appointment:", error);
      message.error("Failed to load appointment details");
      setLoading(false);
    }
  };

  const fetchChatRooms = async () => {
    try {
      setLoading(true);
      const response = await messagesAPI.apiGetChatRooms();
      const roomData = response?.data?.find(
        (item) => item?.patientInfo?.id === appointment?.patientId
      );
      setSelectedRoom(roomData);
    } catch (error) {
      console.error("Error fetching chat rooms:", error);
      antMessage.error("Failed to load conversations");
    } finally {
      setLoading(false);
    }
  };

  const fetchChatHistory = async (roomId, patientId) => {
    try {
      setLoadingMessages(true);
      const response = await messagesAPI.apiGetChatHistory(
        selectedRoom?.id,
        patientId
      );
      const messagesData = response?.data?.data || [];
      setMessages(messagesData.reverse());
    } catch (error) {
      console.error("Error fetching chat history:", error);
      antMessage.error("Failed to load messages");
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    fetchAppointmentAndInitialize();
    return () => {
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    socketService.connect();

    if (appointment?.patientId) {
      fetchChatRooms();
    }

    return () => {
      if (selectedRoom) {
        socketService.leaveChatRoom({
          roomType: 1,
          userId: currentUserId,
          patientId: selectedRoom.patientInfo?.id,
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointment]);

  useEffect(() => {
    if (!currentUserId) return;

    const handleNewMessage = (data) => {
      console.log("📨 New message received:", data);

      if (selectedRoom && data.roomId === selectedRoom.id) {
        setMessages((prev) => {
          const filtered = prev?.filter((msg) => !msg?.isOptimistic);
          const exists = filtered?.some((msg) => msg?.id === data?.message?.id);
          if (exists) return prev;
          return [...filtered, data];
        });
      }
    };

    const handleMessageSent = (data) => {
      console.log("✅ Message sent confirmation:", data);

      if (selectedRoom && data.roomId === selectedRoom.id) {
        setMessages((prev) => {
          const filtered = prev.filter((msg) => !msg.isOptimistic);
          const exists = filtered.some((msg) => msg.id === data.message.id);
          if (exists) return prev;
          return [...filtered, data.message];
        });
      }
      setSending(false);
    };

    const handleTyping = (data) => {
      if (
        selectedRoom &&
        data.roomId === selectedRoom.id &&
        data.userId !== currentUserId
      ) {
        setTypingUsers([data.userId]);

        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }

        typingTimeoutRef.current = setTimeout(() => {
          setTypingUsers([]);
        }, 3000);
      }
    };

    socketService.onNewMessage(handleNewMessage);
    socketService.onMessageSent(handleMessageSent);
    socketService.onTyping(handleTyping);

    return () => {
      socketService.off("newMessage");
      socketService.off("messageSent");
      socketService.off("userTyping");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoom?.id, currentUserId]);

  useEffect(() => {
    if (selectedRoom && currentUserId) {
      fetchChatHistory(selectedRoom.id, selectedRoom.patientInfo?.id);

      socketService.createChatRoom({
        roomType: 1,
        userId: currentUserId,
        patientId: selectedRoom.patientInfo?.id,
        roomId: selectedRoom?.id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoom, currentUserId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedRoom || !currentUserId) return;

    const messageContent = messageInput?.trim();
    const optimisticId = `temp-${Date.now()}`;

    const optimisticMessage = {
      id: optimisticId,
      content: messageContent,
      messageType: 1,
      file: null,
      createdAt: Math.floor(Date.now() / 1000),
      sender: {
        id: currentUserId,
        name: currentUser?.displayName || currentUser?.firstName,
        profileImage: currentUser?.profileImage,
        role: currentUser?.role,
      },
      isOptimistic: true,
    };

    try {
      setSending(true);
      setMessages((prev) => [...prev, optimisticMessage]);
      setMessageInput("");

      socketService.sendMessage({
        userId: currentUserId,
        roomId: selectedRoom?.id,
        roomType: 1,
        patientId: selectedRoom?.patientInfo?.id,
        messageType: 1,
        content: messageContent,
      });

      setTimeout(() => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === optimisticId ? { ...msg, isOptimistic: false } : msg
          )
        );
        setSending(false);
      }, 5000);
    } catch (error) {
      console.error("Error sending message:", error);
      antMessage.error("Failed to send message");
      setMessages((prev) => prev.filter((msg) => msg.id !== optimisticId));
      setSending(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!selectedRoom || !currentUserId) {
      antMessage.error("Please select a conversation first");
      return false;
    }

    try {
      // Show loading message
      antMessage.loading("Uploading file...", 0);

      // Create FormData
      const formData = new FormData();
      formData.append("file", file);
      formData.append("roomId", selectedRoom.id);
      formData.append("patientId", selectedRoom.patientInfo?.id);

      // Upload file to server
      const response = await messagesAPI.apiUploadChatFile(formData);

      antMessage.destroy(); // Clear loading message
      antMessage.success("File uploaded successfully");

      // Get file URL from response
      const fileUrl = response?.data?.fileUrl || response?.fileUrl;
      const fileName = file.name;

      // Send message with file
      const optimisticId = `temp-${Date.now()}`;
      const optimisticMessage = {
        id: optimisticId,
        content: fileName,
        messageType: 2, // File type
        file: fileUrl,
        fileUrl: fileUrl,
        createdAt: Math.floor(Date.now() / 1000),
        sender: {
          id: currentUserId,
          name: currentUser.displayName || currentUser.firstName,
          profileImage: currentUser.profileImage,
          role: currentUser.role,
        },
        isOptimistic: true,
      };

      // Add to messages
      setMessages((prev) => [...prev, optimisticMessage]);

      socketService.sendMessage({
        userId: currentUserId,
        roomId: selectedRoom.id,
        roomType: 1,
        messageType: 2,
        fileUrl: fileUrl,
        content: null,
      });
    } catch (error) {
      antMessage.destroy();
      console.error("Error uploading file:", error);
      antMessage.error("Failed to upload file");
    }

    // Return false to prevent default upload behavior
    return false;
  };

  const handleTyping = () => {
    if (selectedRoom) {
      socketService.emitTyping(selectedRoom.id);
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  // Add this useEffect to play video when track is available
  useEffect(() => {
    console.log("🎥 Local video track changed:", localVideoTrack);
    console.log("📹 Local video ref:", localVideoRef.current);

    if (localVideoTrack && localVideoRef.current && isVideoEnabled) {
      console.log("▶️ Playing local video...");
      try {
        localVideoTrack.play(localVideoRef.current, { fit: "cover" });
        console.log("✅ Local video playing successfully");
      } catch (error) {
        console.error("❌ Error playing local video:", error);
      }
    }
  }, [localVideoTrack, isVideoEnabled]);

  useEffect(() => {
    if (isJoined) {
      callTimerRef.current = setInterval(() => {
        setCallDuration((prev) => prev + 1);
      }, 1000);
    }
    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, [isJoined]);

  const [patientDetails, setPatientDetails] = useState(null); // Add this state

  // Keep your original initializeCall function
  const initializeCall = async (appointmentData = null) => {
    try {
      setLoading(true);
      agoraService.init(process.env.REACT_APP_AGORA_APP_ID);

      agoraService.onUserPublished = handleUserPublished;
      agoraService.onUserUnpublished = handleUserUnpublished;
      agoraService.onUserJoined = handleUserJoined;
      agoraService.onUserLeft = handleUserLeft;

      // Generate channel name based on backend format: patient_id_appointment_id
      const patientId =
        appointmentData?.patientId || appointmentData?.patient?.id;
      const channelName = `${patientId}_${appointmentId}`;

      console.log("🔑 Requesting Agora token for channel:", channelName);

      // Get token from backend
      const tokenResponse = await apiJoinVideoCall(channelName);
      const { token, uid } = tokenResponse?.data;

      setPatientDetails(tokenResponse?.data?.patientDetails);

      await agoraService.join(channelName, token, uid);

      // Get device list first
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasCamera = devices.some((d) => d.kind === "videoinput");
      const hasMic = devices.some((d) => d.kind === "audioinput");

      if (!hasCamera) {
        message.warning("No camera detected. Joining call with audio only.");
        setIsVideoEnabled(false);
      }

      if (!hasMic) {
        message.warning("No microphone detected.");
        setIsAudioEnabled(false);
      }

      // Create and publish tracks based on availability
      const tracks = await agoraService.createAndPublishTracks(
        hasCamera && isVideoEnabled,
        hasMic && isAudioEnabled
      );

      if (tracks?.videoTrack) {
        setLocalVideoTrack(tracks.videoTrack);
      }

      setIsJoined(true);
      message.success("Joined video call successfully");
    } catch (error) {
      console.error("Error initializing call:", error);
      message.error(error?.message || "Failed to join video call");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const MEDICAL_ICONS = {
    heart: "/icon/heart_icon.svg",
    kidney: "/icon/nephrology_icon.svg",
    lungs: "/icon/lungs_icon.svg",
    stomach: "/icon/stomach_icon.svg",
    joints: "/icon/joint_icon.svg",
    metabolism: "/icon/metabolism_icon.svg",
    brain: "/icon/brain_icon.svg",
    cancer: "/icon/cancer_icon.svg",
    smoking: "/icon/addiction_icon.svg",
    glp1: "/icon/diabetes_icon.svg",
  };

  const getMedicalHistory = () => {
    if (!patientDetails) {
      return [
        { key: "heart", label: "Heart", icon: MEDICAL_ICONS.heart, data: null },
        {
          key: "kidney",
          label: "Kidney",
          icon: MEDICAL_ICONS.kidney,
          data: null,
        },
        { key: "lungs", label: "Lungs", icon: MEDICAL_ICONS.lungs, data: null },
        {
          key: "stomach",
          label: "Digestive",
          icon: MEDICAL_ICONS.stomach,
          data: null,
        },
        {
          key: "joints",
          label: "Joints",
          icon: MEDICAL_ICONS.joints,
          data: null,
        },
        {
          key: "metabolism",
          label: "Metabolism",
          icon: MEDICAL_ICONS.metabolism,
          data: null,
        },
        {
          key: "brain",
          label: "Neurology",
          icon: MEDICAL_ICONS.brain,
          data: null,
        },
        {
          key: "cancer",
          label: "Cancer",
          icon: MEDICAL_ICONS.cancer,
          data: null,
        },
        {
          key: "smoking",
          label: "Lifestyle",
          icon: MEDICAL_ICONS.smoking,
          data: null,
        },
        { key: "glp1", label: "GLP-1", icon: MEDICAL_ICONS.glp1, data: null },
      ];
    }

    return [
      {
        key: "heart",
        label: "Heart",
        icon: MEDICAL_ICONS.heart,
        data: patientDetails.heartConditions,
      },
      {
        key: "kidney",
        label: "Kidney",
        icon: MEDICAL_ICONS.kidney,
        data: patientDetails.kidneyConditions,
      },
      {
        key: "lungs",
        label: "Lungs",
        icon: MEDICAL_ICONS.lungs,
        data: patientDetails.lungDisorders,
      },
      {
        key: "stomach",
        label: "Digestive",
        icon: MEDICAL_ICONS.stomach,
        data: patientDetails.digestiveConditions,
      },
      {
        key: "joints",
        label: "Joints",
        icon: MEDICAL_ICONS.joints,
        data: patientDetails.jointMobilityConditions,
      },
      {
        key: "metabolism",
        label: "Metabolism",
        icon: MEDICAL_ICONS.metabolism,
        data: patientDetails.metabolismDisorders,
      },
      {
        key: "brain",
        label: "Neurology",
        icon: MEDICAL_ICONS.brain,
        data: patientDetails.neurologyDisorders,
      },
      {
        key: "cancer",
        label: "Cancer",
        icon: MEDICAL_ICONS.cancer,
        data: patientDetails.cancerHistory,
      },
      {
        key: "smoking",
        label: "Lifestyle",
        icon: MEDICAL_ICONS.smoking,
        data: [
          patientDetails.smoking && `Smoking: ${patientDetails.smoking}`,
          patientDetails.alcohol && `Alcohol: ${patientDetails.alcohol}`,
          patientDetails.recreationalDrugUse &&
            `Drugs: ${patientDetails.recreationalDrugUse}`,
        ].filter(Boolean),
      },
      {
        key: "glp1",
        label: "GLP-1",
        icon: MEDICAL_ICONS.glp1,
        data: patientDetails.glp1PastHistory
          ? [patientDetails.glp1PastHistory]
          : null,
      },
    ];
  };

  const handleUserPublished = async (user, mediaType) => {
    if (mediaType === "video") {
      setRemoteUsers((prev) => {
        const exists = prev.find((u) => u.uid === user.uid);
        if (!exists) {
          return [...prev, user];
        }
        return prev;
      });

      setTimeout(() => {
        const videoElement = remoteVideoRefs.current[user.uid];
        if (videoElement && user.videoTrack) {
          console.log("▶️ Playing remote video for user:", user.uid);
          user.videoTrack.play(videoElement);
        }
      }, 100);
    }
  };

  const handleUserUnpublished = (user, mediaType) => {
    if (mediaType === "video") {
      setRemoteUsers((prev) => prev.filter((u) => u.uid !== user.uid));
    }
  };

  const handleUserJoined = (user) => {
    console.log("👋 User joined:", user.uid);
  };

  const handleUserLeft = (user) => {
    console.log("👋 User left:", user.uid);
    setRemoteUsers((prev) => prev.filter((u) => u.uid !== user.uid));
  };

  const toggleVideo = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const hasCamera = devices.some((d) => d.kind === "videoinput");

    if (!hasCamera) {
      message.error("No camera available on this device.");
      return;
    }

    try {
      const enabled = !isVideoEnabled;
      console.log("📹 Toggling video to:", enabled);

      setIsVideoEnabled(enabled);
      await agoraService.toggleVideo(enabled);

      // If turning video back on, replay it after a small delay
      if (enabled && localVideoTrack && localVideoRef.current) {
        setTimeout(() => {
          if (localVideoRef.current && localVideoTrack) {
            try {
              localVideoTrack.play(localVideoRef.current, { fit: "cover" });
            } catch (err) {
              console.warn("Play interrupted:", err);
            }
          }
        }, 100);
      } else if (!enabled && localVideoTrack) {
        // Stop playing when video is disabled
        try {
          localVideoTrack.stop();
        } catch (err) {
          console.warn("Stop error:", err);
        }
      }
    } catch (error) {
      console.error("Error toggling video:", error);
      message.error("Failed to toggle video");
      // Revert the state if it failed
      setIsVideoEnabled(!isVideoEnabled);
    }
  };

  const toggleAudio = async () => {
    try {
      const enabled = !isAudioEnabled;
      await agoraService.toggleAudio(enabled);
      setIsAudioEnabled(enabled);
    } catch (error) {
      message.error("Failed to toggle audio");
    }
  };

  const endCall = async () => {
    try {
      await cleanup();
      message.success("Call ended");
      navigate("/appointments");
    } catch (error) {
      message.error("Error ending call");
    }
  };

  const cleanup = async () => {
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
    }

    // Stop local tracks
    if (localVideoTrack) {
      localVideoTrack.stop();
      localVideoTrack.close();
    }

    await agoraService.leave();
    setIsJoined(false);
    setLocalVideoTrack(null);
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <Spin size="large" />
          <p className="text-gray-600 mt-4">Initializing video call...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-white">
      <div className="flex-1 flex overflow-hidden">
        {/* Left Side - Video Call Area (70%) */}
        {/* Video Area */}
        <div className="w-[70%] h-[80%] p-6 relative flex flex-col gap-4">
          {/* TOP: Patient Video (large) */}
          <div className="flex-1 bg-gray-900 rounded-lg overflow-hidden relative">
            {remoteUsers.length > 0 ? (
              // if there are remote users, render the first remote stream full area
              remoteUsers.map((user) => (
                <div
                  key={user.uid}
                  ref={(el) => (remoteVideoRefs.current[user.uid] = el)}
                  className="w-full h-full"
                  style={{ position: "relative" }}
                />
              ))
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center">
                  <Avatar size={120} className="bg-purple-600 mb-4">
                    <UserOutlined className="text-6xl" />
                  </Avatar>
                  <p className="text-white text-xl">
                    Waiting for patient to join...
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* BOTTOM: Doctor Video (smaller strip) */}
          <div className="relative h-[40%] rounded-lg overflow-hidden bg-gray-800 flex items-center justify-center">
            {isVideoEnabled && localVideoTrack ? (
              <div className="w-full h-full relative">
                {/* local video container (fills the strip) */}
                <div
                  ref={localVideoRef}
                  id="local-video-container"
                  className="w-full h-full bg-gray-700 object-cover"
                />
                {/* 'You' label */}
                <div className="absolute bottom-2 left-3 bg-black bg-opacity-70 px-2 py-0.5 rounded text-white text-xs">
                  You
                </div>
              </div>
            ) : (
              <div className="w-full h-full flex gap-2 flex-col items-center justify-center bg-gray-700">
                <Avatar size={60} className="bg-purple-600">
                  <UserOutlined className="text-4xl" />
                </Avatar>
                <span className="text-white text-xs">You</span>
              </div>
            )}

            {/* Overlay Controls - centered above doctor video */}
            <div className="absolute left-1/2 transform -translate-x-1/2 bottom-[0px] mb-4 z-20">
              <div className="flex items-center gap-4 bg-transparent">
                <Tooltip
                  title={isVideoEnabled ? "Turn off camera" : "Turn on camera"}
                >
                  <Button
                    shape="circle"
                    size="large"
                    icon={<VideoCameraOutlined />}
                    onClick={toggleVideo}
                    className={`w-14 h-14 ${
                      isVideoEnabled
                        ? "bg-white/90 hover:bg-white"
                        : "bg-red-500 hover:bg-red-600 text-white"
                    } shadow-lg`}
                  />
                </Tooltip>

                <Tooltip
                  title={
                    isAudioEnabled ? "Mute microphone" : "Unmute microphone"
                  }
                >
                  <Button
                    shape="circle"
                    size="large"
                    icon={
                      isAudioEnabled ? (
                        <AudioOutlined />
                      ) : (
                        <AudioMutedOutlined />
                      )
                    }
                    onClick={toggleAudio}
                    className={`w-14 h-14 ${
                      isAudioEnabled
                        ? "bg-white/90 hover:bg-white"
                        : "bg-red-500 hover:bg-red-600 text-white"
                    } shadow-lg`}
                  />
                </Tooltip>

                <Tooltip title="End call">
                  <Button
                    danger
                    shape="circle"
                    size="large"
                    icon={<PhoneOutlined className="rotate-135" />}
                    onClick={endCall}
                    className="w-14 h-14 bg-red-500 hover:bg-red-600 shadow-lg"
                  />
                </Tooltip>
              </div>
            </div>
          </div>
        </div>

        {/* spacer to prevent controls overlap with bottom page elements (optional) */}
        <div style={{ height: 36 }} />

        {/* Right Side - Medical Record Summary (30%) */}
        <div className="w-[30%] bg-white border-l overflow-y-auto">
          <div className="p-6">
            {/* Patient Overview */}
            <Card className="mb-4 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Patient's Overview
              </h3>
              <div className="flex gap-3 w-full">
                {/* Face Photo */}
                <div className="relative group">
                  <div className="relative w-full h-32 bg-gray-200 rounded overflow-hidden">
                    <div
                      className="flex-1 object-cover"
                      style={{
                        minWidth: "150px",
                        height: "297px",
                        borderRadius: "20px",
                      }}
                    >
                      {patientDetails?.facePhoto ? (
                        <img
                          src={patientDetails.facePhoto}
                          alt="Face"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Avatar
                            size={64}
                            src={appointment?.profileImage}
                            className="bg-purple-600"
                          >
                            {appointment?.fullName?.charAt(0)?.toUpperCase()}
                          </Avatar>
                        </div>
                      )}
                    </div>

                    {/* Hover Overlay for Name, Gender, DOB */}
                    {patientDetails && (
                      <div className="absolute inset-0 bg-black bg-opacity-80 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                        <div className="text-white text-center space-y-2 px-4">
                          <div className="text-sm">
                            <span className="font-semibold">Name:</span>{" "}
                            {patientDetails.name}
                          </div>
                          <div className="text-sm">
                            <span className="font-semibold">Gender:</span>{" "}
                            {patientDetails.gender}
                          </div>
                          <div className="text-sm">
                            <span className="font-semibold">DOB:</span>{" "}
                            {formatDate(patientDetails.dateOfBirth)}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Full Body Photo */}
                <div className="relative group">
                  <div className="relative w-full h-32 bg-gray-200 rounded overflow-hidden">
                    <div
                      className="flex-1 object-cover"
                      style={{
                        minWidth: "150px",
                        height: "297px",
                        borderRadius: "20px",
                      }}
                    >
                      {patientDetails?.bodyImage ? (
                        <img
                          src={patientDetails.bodyImage}
                          alt="Full Body"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <UserOutlined className="text-4xl text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Hover Overlay for BMI, Height, Weight */}
                    {patientDetails && (
                      <div className="absolute inset-0 bg-black bg-opacity-80 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                        <div className="text-white text-center space-y-2">
                          <div className="text-sm">
                            <span className="font-semibold">BMI:</span>{" "}
                            {patientDetails.bmi || "N/A"}
                          </div>
                          <div className="text-sm">
                            <span className="font-semibold">Height:</span>{" "}
                            {patientDetails.height || "N/A"} cm
                          </div>
                          <div className="text-sm">
                            <span className="font-semibold">Weight:</span>{" "}
                            {patientDetails.weight || "N/A"} kg
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>

            {/* Patient's Medical History */}
            <Card className="mb-4 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Patient's Medical History
              </h3>

              <div className="grid grid-cols-5 gap-3">
                {getMedicalHistory().map((item) => {
                  // prepare popover content
                  const hasData = item.data && item.data.length > 0;
                  const popoverContent = hasData ? (
                    <div className="max-w-xs text-left">
                      {/* Example structured content — adapt keys as needed */}
                      {Array.isArray(item.data) ? (
                        item.data.map((entry, idx) => (
                          <div key={idx} className="mb-2">
                            <div className="text-sm font-semibold">
                              {item.label}
                            </div>
                            <div className="text-xs text-gray-600">{entry}</div>
                          </div>
                        ))
                      ) : (
                        <div className="text-xs text-gray-600">
                          {String(item.data)}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-600">
                      No conditions reported
                    </div>
                  );

                  return (
                    <div
                      className="flex flex-col items-center justify-center p-3 rounded-lg bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                      aria-label={item.label}
                    >
                      {/* Icon placeholder - replace with SVG later */}
                      <div className="w-10 h-10 flex items-center justify-center rounded-md">
                        <Popover
                          key={item.key}
                          content={popoverContent}
                          trigger="hover"
                          placement="bottomLeft"
                          arrow={false}
                        >
                          <img
                            src={item.icon}
                            alt={item.label}
                            className="w-6 h-6 object-contain"
                          />
                        </Popover>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Consultation Notes */}
            {appointment?.consultationNotes && (
              <Card className="mb-4 shadow-sm">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Consultation Notes
                </h3>
                <div className="flex gap-3">
                  {appointment?.consultationNotes}
                </div>
              </Card>
            )}

            {/* Live Chat Section */}
            <Card className="mb-4 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Live Chat
              </h3>
              <div className="flex-1 flex flex-col">
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-gray-200 flex items-center gap-3">
                    <Avatar size={28} src={selectedRoom?.profileImage}>
                      {selectedRoom?.fullName?.charAt(0).toUpperCase()}
                    </Avatar>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-base">
                        {selectedRoom?.patientInfo?.fullName}
                      </h3>
                    </div>
                  </div>

                  {/* Messages Area */}
                  <div className="flex-1 overflow-y-auto p-6 bg-purple-50 max-h-[300px]">
                    {loadingMessages ? (
                      <div className="flex justify-center items-center h-full">
                        <Spin size="large" />
                      </div>
                    ) : (
                      <>
                        {messages.map((msg, index) => {
                          const senderId =
                            msg?.sender?.id ||
                            msg?.senderId ||
                            msg?.userId ||
                            null;
                          const isOwn = senderId === currentUserId;

                          const createdAt =
                            msg?.createdAt ||
                            msg?.timestamp ||
                            msg?.time ||
                            Math.floor(Date.now() / 1000);

                          const formattedTime = new Date(
                            createdAt * 1000
                          ).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          });

                          const prev = messages[index - 1];
                          const prevDate =
                            prev?.createdAt ||
                            prev?.timestamp ||
                            Math.floor(Date.now() / 1000);

                          const showDate =
                            !prev ||
                            new Date(createdAt * 1000).toDateString() !==
                              new Date(prevDate * 1000).toDateString();
                          return (
                            <React.Fragment key={msg?.id || index}>
                              {showDate && (
                                <div className="text-center mb-4">
                                  <span className="text-xs bg-white px-3 py-1 rounded-full">
                                    {new Date(createdAt * 1000).toDateString()}
                                  </span>
                                </div>
                              )}

                              <div
                                className={`flex mb-3 ${
                                  isOwn ? "justify-end" : "justify-start"
                                }`}
                              >
                                <div
                                  className={`p-3 max-w-md rounded-2xl ${
                                    isOwn
                                      ? "bg-purple-600 text-white"
                                      : "bg-white text-black"
                                  }`}
                                >
                                  {msg?.messageType === 2 ? (
                                    <img
                                      src={msg?.file}
                                      alt="attachment"
                                      className="max-w-[220px] w-full max-h-60 object-cover rounded-xl cursor-pointer"
                                      onClick={() =>
                                        window.open(msg?.file, "_blank")
                                      }
                                    />
                                  ) : (
                                    <span className="whitespace-pre-line">
                                      {msg?.content}
                                    </span>
                                  )}

                                  <div className="text-[10px] text-right mt-1 opacity-80">
                                    {formattedTime}{" "}
                                    {isOwn &&
                                      (msg?.seen
                                        ? "✓✓"
                                        : msg?.delivered
                                        ? "✓"
                                        : "•")}
                                  </div>
                                </div>
                              </div>
                            </React.Fragment>
                          );
                        })}

                        {typingUsers.length > 0 && (
                          <div className="flex items-end gap-2 mb-4">
                            <Avatar
                              size={32}
                              src={selectedRoom?.patientInfo?.profileImage}
                            >
                              {selectedRoom?.patientInfo?.fullName
                                ?.charAt(0)
                                .toUpperCase()}
                            </Avatar>
                            <div className="bg-white px-4 py-3 rounded-2xl rounded-bl-sm shadow-sm">
                              <div className="flex gap-1">
                                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></span>
                                <span
                                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                  style={{ animationDelay: "0.1s" }}
                                ></span>
                                <span
                                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                  style={{ animationDelay: "0.2s" }}
                                ></span>
                              </div>
                            </div>
                          </div>
                        )}

                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </div>

                  {/* Message Input */}
                  <div className="p-4 border-t border-gray-200 bg-white">
                    {selectedRoom?.isPassive ? (
                      <div className="flex items-center justify-center p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-500">
                          You cannot send messages to this patient. This
                          conversation is managed by another doctor.
                        </p>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Input
                          placeholder="Type your message here..."
                          value={messageInput}
                          onChange={(e) => {
                            setMessageInput(e.target.value);
                            handleTyping();
                          }}
                          onPressEnter={handleSendMessage}
                          size="large"
                          className="flex-1"
                          suffix={
                            <Upload
                              beforeUpload={handleFileUpload}
                              showUploadList={false}
                            >
                              <Button
                                type="text"
                                icon={<PaperClipOutlined />}
                              />
                            </Upload>
                          }
                        />
                        <Button
                          type="primary"
                          icon={<SendOutlined />}
                          size="large"
                          onClick={handleSendMessage}
                          loading={sending}
                          disabled={!messageInput.trim() || sending}
                          className="bg-purple-600 hover:bg-purple-700 border-purple-600"
                        >
                          Send
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoCall;
